/**
 * OrbitBounds — viewport overlay visualizing the region the ORBIT camera may
 * occupy in the published viewer: a spherical-shell wedge around the pivot,
 * bounded by
 *   - radius  ∈ [zoomMinM, zoomMaxM]          (authored zoom band, meters)
 *   - elevation ∈ [pitchMinDeg, ~85°]         (pitch floor keeps it above ground)
 *   - azimuth full 360°
 *
 * Pivot = the saved start-pose target (what the viewer will orbit), falling
 * back to the live editor-camera target — the same rule buildCamera uses at
 * export, so what you see is what ships. Values track the Viewer panel live.
 *
 * Toggled from the scene panel ("Show bounds"); renders on the gizmo layer
 * with the same line-mesh + debug-shader pattern as WalkArea/Markers.
 */

import {
    Entity,
    Mesh,
    MeshInstance,
    PRIMITIVE_LINES,
    SEMANTIC_COLOR,
    SEMANTIC_POSITION,
    ShaderMaterial,
    Vec3
} from 'playcanvas';
import { seedLineMesh } from './line-mesh';

import { Element, ElementType } from '../element';
import type { Events } from '../events';
import { vertexShader, fragmentShader } from '../shaders/debug-shader';

type RGB = [number, number, number];
const FLOOR_RGB: RGB = [255, 170, 40];   // orange — the pitch-floor ring (hard limit)
const INNER_RGB: RGB = [0, 140, 200];    // dim cyan — closest-zoom shell
const OUTER_RGB: RGB = [0, 210, 255];    // bright cyan — farthest-zoom shell
const SPOKE_RGB: RGB = [90, 170, 210];   // faint meridian arcs
const PIVOT_RGB: RGB = [255, 255, 255];  // pivot cross

const ELEV_TOP = (85 * Math.PI) / 180;   // draw arcs up to ~85° (pole clutter past that)
const RING_SEGS = 64;
const ARC_STEPS = 18;
const SPOKES = 12;

class OrbitBounds extends Element {
    entity!: Entity;
    mesh!: Mesh;
    material!: ShaderMaterial;
    meshInstance!: MeshInstance;

    private _visible = false;
    private _lastKey = '';

    constructor() {
        super(ElementType.debug);
    }

    add() {
        const { scene } = this;
        this.material = new ShaderMaterial({
            uniqueName: 'orbitBoundsMaterial',
            attributes: {
                vertex_position: SEMANTIC_POSITION,
                vertex_color: SEMANTIC_COLOR
            },
            vertexWGSL: vertexShader,
            fragmentWGSL: fragmentShader
        });
        this.material.depthWrite = false;
        this.material.depthTest = false;
        this.material.update();

        this.mesh = new Mesh(scene.graphicsDevice);
        this.mesh.primitive[0] = { baseVertex: 0, type: PRIMITIVE_LINES, base: 0, count: 0 };
        seedLineMesh(this.mesh);
        this.meshInstance = new MeshInstance(this.mesh, this.material, null);
        this.meshInstance.cull = false;

        this.entity = new Entity('orbitBounds');
        this.entity.addComponent('render', {
            meshInstances: [this.meshInstance],
            layers: [scene.gizmoLayer.id]
        });
        scene.app.root.addChild(this.entity);

        const events = scene.events;
        // The editor renders on demand — force a frame so onPreRender runs and
        // the overlay reacts immediately to the toggle / panel edits.
        events.on('orbitBounds.setVisible', (v: boolean) => {
            this._visible = v;
            this._lastKey = '';
            scene.forceRender = true;
        });
        events.on('viewer.settingsChanged', () => {
            if (this._visible) scene.forceRender = true;
        });
        events.function('orbitBounds.visible', () => this._visible);
    }

    destroy() {
        this.entity?.destroy();
    }

    onPreRender() {
        const events = this.scene.events;
        const settings = this._visible ? events.invoke('viewer.settings') : null;
        const show = !!(this._visible && settings && settings.navigation === 'rotating' && this.scene.camera.renderOverlays);
        this.entity.enabled = show;
        if (!show) { this._lastKey = ''; return; }

        // Pivot: ALWAYS the world origin — the grid-axes crossing. Orbit
        // scenes are authored by centering the property on the origin, and
        // buildCamera exports initial.target = [0,0,0] for rotating nav, so
        // the published viewer orbits exactly where these rings render.
        const t = { x: 0, y: 0, z: 0 };

        const o = settings.orbit ?? {};
        const rMin = (o.zoomMinM ?? 0) > 0 ? o.zoomMinM as number : 0;
        const rMax = (o.zoomMaxM ?? 0) > 0 ? o.zoomMaxM as number : 0;
        const pitchMin = ((o.pitchMinDeg ?? 16) * Math.PI) / 180;

        // Rebuild only when something changed (pivot moves / values edited).
        const key = `${t.x.toFixed(2)},${t.y.toFixed(2)},${t.z.toFixed(2)}|${rMin}|${rMax}|${pitchMin.toFixed(3)}`;
        if (key === this._lastKey) return;
        this._lastKey = key;
        this.rebuildMesh(new Vec3(t.x, t.y, t.z), rMin, rMax, pitchMin);
    }

    private rebuildMesh(c: Vec3, rMin: number, rMax: number, pitchMin: number) {
        const pos: number[] = [];
        const col: number[] = [];
        const pushLine = (ax: number, ay: number, az: number, bx: number, by: number, bz: number, rgb: RGB) => {
            pos.push(ax, ay, az, bx, by, bz);
            for (let k = 0; k < 2; k++) col.push(rgb[0], rgb[1], rgb[2], 255);
        };
        // point on the orbit sphere: elevation el (from horizontal), azimuth az
        const pt = (r: number, el: number, az: number) => ({
            x: c.x + r * Math.cos(el) * Math.sin(az),
            y: c.y + r * Math.sin(el),
            z: c.z + r * Math.cos(el) * Math.cos(az)
        });
        // latitude ring at elevation el
        const pushRing = (r: number, el: number, rgb: RGB) => {
            let prev = pt(r, el, 0);
            for (let i = 1; i <= RING_SEGS; i++) {
                const cur = pt(r, el, (i / RING_SEGS) * Math.PI * 2);
                pushLine(prev.x, prev.y, prev.z, cur.x, cur.y, cur.z, rgb);
                prev = cur;
            }
        };
        // meridian arc from pitchMin up to ELEV_TOP at azimuth az
        const pushArc = (r: number, az: number, rgb: RGB) => {
            let prev = pt(r, pitchMin, az);
            for (let i = 1; i <= ARC_STEPS; i++) {
                const el = pitchMin + (i / ARC_STEPS) * (ELEV_TOP - pitchMin);
                const cur = pt(r, el, az);
                pushLine(prev.x, prev.y, prev.z, cur.x, cur.y, cur.z, rgb);
                prev = cur;
            }
        };
        const pushShell = (r: number, rgb: RGB) => {
            pushRing(r, pitchMin, FLOOR_RGB);                       // the hard floor ring
            const span = ELEV_TOP - pitchMin;
            for (const f of [0.33, 0.66]) pushRing(r, pitchMin + span * f, rgb);
            for (let s = 0; s < SPOKES; s++) pushArc(r, (s / SPOKES) * Math.PI * 2, SPOKE_RGB);
        };

        // pivot cross
        const H = Math.max(0.25, (rMax || rMin || 4) * 0.02);
        pushLine(c.x - H, c.y, c.z, c.x + H, c.y, c.z, PIVOT_RGB);
        pushLine(c.x, c.y - H, c.z, c.x, c.y + H, c.z, PIVOT_RGB);
        pushLine(c.x, c.y, c.z - H, c.x, c.y, c.z + H, PIVOT_RGB);

        if (rMin > 0) pushShell(rMin, INNER_RGB);
        if (rMax > 0 && rMax !== rMin) pushShell(rMax, OUTER_RGB);
        // wedge edges: connect the two floor rings so the band reads as a solid
        if (rMin > 0 && rMax > rMin) {
            for (let s = 0; s < SPOKES; s++) {
                const az = (s / SPOKES) * Math.PI * 2;
                const a = pt(rMin, pitchMin, az);
                const b = pt(rMax, pitchMin, az);
                pushLine(a.x, a.y, a.z, b.x, b.y, b.z, FLOOR_RGB);
            }
        }

        if (pos.length === 0) {
            this.mesh.primitive[0].count = 0;
            this.entity.enabled = false;
            return;
        }
        this.mesh.setPositions(pos);
        this.mesh.setColors32(new Uint8Array(col));
        this.mesh.update(PRIMITIVE_LINES);
        this.entity.enabled = this.mesh.primitive[0].count > 0;
    }
}

export { OrbitBounds };
